import React, { useEffect, useRef, useState } from 'react';
import {
    useTicker,
    createTrailNodes,
    GameResultOverlay,
    GameShell,
} from '../shared';

function TrailMakingGame({
    copy = {},
    initialLevel = 4,
    onRunComplete = null,
    showResults = true,
    ...props
}) {
    const level = Math.max(1, Math.min(4, Math.round(Number(initialLevel) || 4)));
    const pairCount = level + 2;
    const completionReportedRef = useRef(false);
    const [nodes, setNodes] = useState(() => createTrailNodes(pairCount));
    const [currentIndex, setCurrentIndex] = useState(0);
    const [mistakes, setMistakes] = useState(0);
    const [startedAt, setStartedAt] = useState(null);
    const [finishedAt, setFinishedAt] = useState(null);
    const [message, setMessage] = useState('ready');
    const now = useTicker(Boolean(startedAt && !finishedAt));
    const done = currentIndex >= nodes.length;

    const reset = () => {
        completionReportedRef.current = false;
        setNodes(createTrailNodes(pairCount));
        setCurrentIndex(0);
        setMistakes(0);
        setStartedAt(null);
        setFinishedAt(null);
        setMessage('ready');
    };

    const chooseNode = (nodeIndex) => {
        if (done) {
            return;
        }

        if (!startedAt) {
            setStartedAt(Date.now());
        }

        const node = nodes[nodeIndex];

        if (node.order === currentIndex) {
            if (currentIndex + 1 >= nodes.length) {
                setFinishedAt(Date.now());
                setMessage('done');
            } else {
                setMessage('good');
            }
            setCurrentIndex((value) => value + 1);
            return;
        }

        setMistakes((value) => value + 1);
        setMessage('miss');
    };

    const elapsedMs = startedAt ? Math.max(0, (finishedAt || now) - startedAt) : 0;
    const metrics = { elapsedMs, level, mistakes };

    useEffect(() => {
        if (!done || completionReportedRef.current) {
            return;
        }

        completionReportedRef.current = true;
        onRunComplete?.(metrics);
    }, [done, elapsedMs, mistakes, onRunComplete]);

    return (
        <GameShell
            {...props}
            copy={copy}
            metrics={metrics}
            tone={done ? 'good' : message}
        >
            <div className="UFOV-brain-games__game-cue">{copy.trailCue}</div>
            <div className="UFOV-brain-games__trail-grid">
                {nodes.map((node, index) => (
                    <button
                        className={`${node.order < currentIndex ? 'is-done' : ''} ${message === 'miss' && node.order === currentIndex ? 'is-missed' : ''}`.trim()}
                        key={node.label}
                        type="button"
                        onClick={() => chooseNode(index)}
                    >
                        {node.label}
                    </button>
                ))}
            </div>
            {done && showResults ? (
                <GameResultOverlay metrics={metrics} onRestart={reset} />
            ) : null}
        </GameShell>
    );
}

export default TrailMakingGame;
